import { useQuery } from '@tanstack/react-query';
import api from '../../http';
import { AgriculturalDefensive } from '@practium/features/products';

interface PaginatedResponse {
	docs: AgriculturalDefensive[];
	totalDocs: number;
	limit: number;
	totalPages: number;
	page: number;
	pagingCounter: number;
	hasPrevPage: boolean;
	hasNextPage: boolean;
	prevPage: number | null;
	nextPage: number | null;
}

interface UseGetDefensivesParams {
	page?: number;
	limit?: number;
	filter?: string;
	manufacturer?: string | string[];
	className?: string | string[];
	nature?: string | string[];
	origin?: string | string[];
	sortAsc?: boolean;
	enabled?: boolean;
}

export const useGetDefensives = ({
	page = 1,
	limit = 100,
	filter,
	manufacturer,
	className,
	nature,
	origin,
	sortAsc,
	enabled = true,
}: UseGetDefensivesParams = {}) => {
	const query = useQuery<PaginatedResponse>({
		queryKey: ['defensives', page, limit, filter, className, nature, origin, sortAsc, manufacturer],
		enabled,
		placeholderData: (previousData) => previousData,
		queryFn: async (): Promise<PaginatedResponse> => {
			const params = new URLSearchParams();
			params.set('page', String(page));
			params.set('limit', String(limit));

			if (filter) params.set('filter', filter);
			if (sortAsc !== undefined) params.set('sortAsc', String(sortAsc));

			const classes = Array.isArray(className) ? className : className ? [className] : [];
			const natures = Array.isArray(nature) ? nature : nature ? [nature] : [];
			const origins = Array.isArray(origin) ? origin : origin ? [origin] : [];
			const manufacturers = Array.isArray(manufacturer) ? manufacturer : manufacturer ? [manufacturer] : [];

			classes.forEach((item) => params.append('class', item));
			natures.forEach((item) => params.append('nature', item));
			origins.forEach((item) => params.append('origin', item));
			manufacturers.forEach((item) => params.append('manufacturer', item));

			const { data } = await api.get<PaginatedResponse>('/agricultural-defensives/findAll', {
				params,
			});
			return data;
		},
	});

	return {
		...query,
		data: query.data?.docs ?? [],
		pagination: query.data
			? {
					totalDocs: query.data.totalDocs,
					totalPages: query.data.totalPages,
					page: query.data.page,
					hasPrevPage: query.data.hasPrevPage,
					hasNextPage: query.data.hasNextPage,
					prevPage: query.data.prevPage,
					nextPage: query.data.nextPage,
				}
			: null,
	};
};
